<template>
  <div
    class="pagination"
    :class="[rootClasses, theme]"
  >
    <nuxt-link
      role="button"
      :to="href(current - 1)"
      class="pagination-previous"
      :disabled="!hasPrev"
      @click.prevent="prev"
      v-if="useNuxtLink"
    ><coconala-icon name="chevron-left" /></nuxt-link>
    <a
      class="pagination-previous"
      :disabled="!hasPrev"
      href="javascript:;"
      @click="$emit('change', current - 1)"
      v-else
    ><coconala-icon name="chevron-left" /></a>
    <nuxt-link
      role="button"
      :to="href(current + 1)"
      class="pagination-next"
      :disabled="!hasNext"
      @click.prevent="next"
      v-if="useNuxtLink"
    ><coconala-icon name="chevron-right" /></nuxt-link>
    <a
      class="pagination-next"
      :disabled="!hasNext"
      href="javascript:;"
      @click="$emit('change', current + 1)"
      v-else
    ><coconala-icon name="chevron-right" /></a>
    <ul
      class="pagination-list"
      v-if="!simple"
    >
      <!--First-->
      <li v-if="hasFirst">
        <nuxt-link
          role="button"
          :to="href(1)"
          class="pagination-link"
          @click.prevent="first"
          v-if="useNuxtLink"
        >
          1
        </nuxt-link>
        <a
          class="pagination-link"
          href="javascript:;"
          @click="$emit('change', 1)"
          v-else
        >
          1
        </a>
      </li>
      <li v-if="hasFirstEllipsis"><span class="pagination-ellipsis">&hellip;</span></li>

      <!--Pages-->
      <li
        v-for="page in pagesInRange"
        :key="page.number"
      >
        <nuxt-link
          role="button"
          :to="href(page.number)"
          class="pagination-link"
          :class="{ 'pagination-link-current': page.isCurrent }"
          @click.prevent="page.click"
          v-if="useNuxtLink"
        >
          {{ page.number }}
        </nuxt-link>
        <a
          class="pagination-link"
          :class="{ 'pagination-link-current': page.isCurrent }"
          href="javascript:;"
          @click="$emit('change', page.number)"
          v-else
        >
          {{ page.number }}
        </a>
      </li>

      <!--Last-->
      <li v-if="hasLastEllipsis && (type !== 'searchServicePc' && type !== 'searchServiceSp')"><span class="pagination-ellipsis">&hellip;</span></li>
      <li v-if="hasLast && (type !== 'searchServicePc' && type !== 'searchServiceSp')">
        <nuxt-link
          :to="href(pageCount)"
          class="pagination-link"
          @click.prevent="last"
          v-if="useNuxtLink"
        >
          {{ pageCount }}
        </nuxt-link>
        <a
          class="pagination-link"
          href="javascript:;"
          @click="$emit('change', pageCount)"
          v-else
        >
          {{ pageCount }}
        </a>
      </li>
    </ul>
    <small
      class="info"
      v-if="simple"
    >
      <template v-if="perPage == 1">
        {{ firstItem }} / {{ total }}
      </template>
      <template v-else>
        {{ firstItem }}-{{ Math.min(current * perPage, total) }} / {{ total }}
      </template>
    </small>
  </div>
</template>

<script>
export default {
  name: 'CPagination',
  props: {
    total: [Number, String],
    perPage: {
      type: [Number, String],
      default: 20
    },
    rangeBefore: {
      type: Number,
      default: 1
    },
    rangeAfter: {
      type: Number,
      default: 1
    },
    current: {
      type: [Number, String],
      default: 1
    },
    theme: {
      type: String,
      default: null
    },
    size: String,
    simple: Boolean,
    rounded: Boolean,
    order: String,
    hasLink: Boolean,
    type: {
      type: String,
      default: ''
    },
    useNuxtLink: {
      type: Boolean,
      default: true
    }
  },
  computed: {
    rootClasses() {
      return [
        this.order,
        this.size,
        {
          'is-simple': this.simple,
          'is-rounded': this.rounded
        }
      ]
    },

    /**
     * Total page size (count).
     */
    pageCount() {
      return Math.ceil(this.total / this.perPage)
    },

    /**
     * First item of the page (count).
     */
    firstItem() {
      const firstItem = this.current * this.perPage - this.perPage + 1
      return firstItem >= 0 ? firstItem : 0
    },

    /**
     * Check if previous button is available.
     */
    hasPrev() {
      return this.current > 1
    },

    /**
     * Check if first page button should be visible.
     */
    hasFirst() {
      return this.current >= 2
    },

    /**
     * Check if first ellipsis should be visible.
     */
    hasFirstEllipsis() {
      return this.current >= this.rangeBefore + 3
    },

    /**
     * Check if last page button should be visible.
     */
    hasLast() {
      return this.current <= this.pageCount - 2
    },

    /**
     * Check if last ellipsis should be visible.
     */
    hasLastEllipsis() {
      return this.current < this.pageCount - 2 && this.current <= this.pageCount - 3
    },

    /**
     * Check if next button is available.
     */
    hasNext() {
      return this.current < this.pageCount
    },

    /**
     * Get near pages, 1 before and 1 after the current.
     * Also add the click event to the array.
     */
    pagesInRange() {
      if (this.simple) return

      let left = Math.max(1, this.current - this.rangeBefore)
      let right = Math.min(this.current + this.rangeAfter, this.pageCount)

      // currentが1の時は1ページを自ページとして表示するためこのまま
      // それ以外の場合は別途1ページへのリンクを表示するので2を最小値とする
      if (left === 1 && this.current > 1) {
        left = 2
      }

      // PCサービス検索でrightが10未満且つpageCountが10より大きい時は10にする
      // ただし、pageCountが10以下の時はpageCountを使う
      if (this.type === 'searchServicePc' && right < 10) {
        if (this.pageCount > 10) {
          right = 10
        } else if (right < this.pageCount) {
          right = this.pageCount
        }
      }

      // SPサービス検索でrightが5未満且つpageCountが5より大きい時は5にする
      // ただし、pageCountが5以下の時はpageCountを使う
      if (this.type === 'searchServiceSp' && right < 5) {
        if (this.pageCount > 5) {
          right = 5
        } else if (right < this.pageCount) {
          right = this.pageCount
        }
      }

      const pages = []
      for (let i = left; i <= right; i++) {
        pages.push({
          number: i,
          isCurrent: this.current === i,
          click: event => {
            if (this.current === i) return
            this.$emit('change', i)
            this.$emit('update:current', i)

            // Set focus on element to keep tab order
            this.$nextTick(() => event.target.focus())
          }
        })
      }
      return pages
    }
  },
  watch: {
    /**
     * If current page is trying to be greater than page count, set to last.
     */
    pageCount(value) {
      if (this.current > value) this.last()
    }
  },
  methods: {
    /**
     * Previous button click listener.
     */
    prev() {
      if (!this.hasPrev) return
      this.$emit('change', this.current - 1)
      this.$emit('update:current', this.current - 1)
    },

    /**
     * First button click listener.
     */
    first() {
      this.$emit('change', 1)
      this.$emit('update:current', 1)
    },

    /**
     * Last button click listener.
     */
    last() {
      this.$emit('change', this.pageCount)
      this.$emit('update:current', this.pageCount)
    },

    /**
     * Next button click listener.
     */
    next() {
      if (!this.hasNext) return
      this.$emit('change', this.current + 1)
      this.$emit('update:current', this.current + 1)
    },

    /**
     * link
     */
    href(page) {
      if (!this.hasLink) return '#'
      return { query: { ...this.$route.query, page } }
    }
  }
}
</script>
<style lang="scss" scoped>
.pagination {
  &-gray {
    .pagination-previous,
    .pagination-next,
    .pagination-link {
      min-width: 32px;
      height: 32px;
      border-radius: 3px;
      color: $color-gray-800;
      background-color: $color-white;
      border-color: $color-gray-300;
    }

    .pagination-previous,
    .pagination-next {
      font-size: 11px;
    }

    .pagination-link {
      &-current {
        background-color: $color-gray-300;
        color: $color-white;
      }
    }
  }
}
</style>
